/* eslint-disable no-restricted-globals */
/* eslint-disable react/react-in-jsx-scope */
import { useState, useEffect } from "react";
import { CircularProgress } from "@mui/material";
import { useSB } from "../../../contexts/SupabaseContext";
import PersonalInfoStep from "../components/shared/forms/PersonalInfoForm";
import AddressInfoStep from "../components/shared/forms/AddressInfoForm";
import DocumentsStep from "../components/shared/forms/DocumentsForm";
import BankingInfoStep from "../components/shared/forms/BankingInfoForm";

/**
 * useUserDetailLogic - Provides state and functions for managing the user detail page.
 *
 * @param {object} props - Component props.
 * @param {object} props.dealer - The dealer object from the database.
 * @param {number} props.dealerId - The dealer ID.
 * @param {object} props.currentUser - The current user object from the database.
 * @param {Function} props.updateUser - Function to call to update the dealer in the database.
 * @param {object} props.bankingDetails - The banking details object from the database.
 * @param {Function} props.refreshDisplayedDetails - Function to call to refresh the displayed dealer details.
 *
 * @return {object} - An object containing the UI state, form state, status state, and functions.
 */
export function useUserDetailLogic({
  dealer,
  dealerId,
  currentUser,
  updateUser,
  bankingDetails,
  refreshDisplayedDetails,
}) {
  const { updateRow } = useSB();

  // UI State
  const [editDealerOpen, setEditDealerOpen] = useState(false);
  const [contractsOpen, setContractsOpen] = useState(false);
  const [changeParentOpen, setChangeParentOpen] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [suppliersModalOpen, setSuppliersModalOpen] = useState(false);
  const [supportModalOpen, setSupportModalOpen] = useState(false);
  const [creditModalOpen, setCreditModalOpen] = useState(false);
  const [alertModalOpen, setAlertModalOpen] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [deletingDealer, setDeletingDealer] = useState(false);
  const [userTypeModalOpen, setUserTypeModalOpen] = useState(false);

  // Form State - With tabs restored
  const [editTabValue, setEditTabValue] = useState(0);
  const [editFormData, setEditFormData] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState(null);

  // Status State
  const [growthLeader, setGrowthLeader] = useState(false);
  const [isLeader, setIsLeader] = useState(false);
  const [isLeaderBlocked, setIsLeaderBlocked] = useState(false);

  // Initialize status states when dealer changes
  useEffect(() => {
    if (dealer) {
      setGrowthLeader(dealer.growth_leader);
      setIsLeader(dealer.is_leader);
      setIsLeaderBlocked(dealer.is_leader_blocked);
    }
  }, [dealer]);

  // Initialize edit form data when dealer or banking details change
  useEffect(() => {
    if (dealer) {
      const formData = {
        firstName: dealer.first_name || "",
        middleName: dealer.middle_name || "",
        surname: dealer.surname || "",
        dateOfBirth: dealer.date_of_birth
          ? new Date(dealer.date_of_birth).getTime() / 1000
          : null,
        refsGender: dealer.refs_gender || "M",
        refsIdentityDocType: dealer.refs_identity_doc_type || "RSAID",
        identificationNo: dealer.identification_no || "",
        nationality: dealer.nationality || "ZAF",
        operatorId: dealer.operator_id || "",
        companyName: dealer.company_name || "",
        email: dealer.email || "",
        phone: dealer.phone ? dealer.phone.replace("+27", "0") : "",
        altPhone: dealer.alt_phone ? dealer.alt_phone.replace("+27", "0") : "",
        province: dealer.province || "",
        street: dealer.street || "",
        suburb: dealer.suburb || "",
        postalCode: dealer.postal_code || "",
        city: dealer.city || "",
        country: dealer.country || "ZAF",
        refsShirtSize: dealer.refs_shirt_size || "",
        refsPaymentType: dealer.refs_payment_type || "bank",
        bank: bankingDetails?.bank || "",
        refsBankAccountType: bankingDetails?.account_type || null,
        bankAccountNumber: bankingDetails?.account_number || "",
        branchCode: bankingDetails?.branch_code || "",
        eWalletLink: dealer.e_wallet_link || "",
        idPhotoUrl: dealer.id_photo_url || "",
        personPhotoUrl: dealer.person_photo_url || "",
        parentDealer: dealer.parent_id || null,
        refsTypeId: dealer.refs_type_id || null,
        passportIssueDate: dealer.passport_issue_date
          ? new Date(dealer.passport_issue_date).getTime() / 1000
          : null,
        passportExpiryDate: dealer.passport_expiry_date
          ? new Date(dealer.passport_expiry_date).getTime() / 1000
          : null,
      };

      setEditFormData(formData);
    }
  }, [dealer, bankingDetails]);

  // Handler functions
  const handleGrowthLeaderChange = event => {
    setGrowthLeader(event.target.checked);

    updateRow("users_master", dealerId, {
      growth_leader: event.target.checked,
      growth_leader_time: event.target.checked ? new Date() : null,
      last_updated_by_auth_id: currentUser.auth_id,
    }).then(result => {
      if (result) {
        refreshDisplayedDetails();
      }
    });
  };

  const handleIsLeader = event => {
    setIsLeader(event.target.checked);

    updateRow("users_master", dealerId, {
      is_leader: event.target.checked,
      is_leader_date: event.target.checked ? new Date() : null,
      last_updated_by_auth_id: currentUser.auth_id,
    }).then(result => {
      if (result) {
        refreshDisplayedDetails();
      }
    });
  };

  const handleIsLeaderBlock = event => {
    setIsLeaderBlocked(event.target.checked);

    updateRow("users_master", dealerId, {
      is_leader_blocked: event.target.checked,
    }).then(result => {
      if (result.error) {
        console.error(result.error.message);
      }
    });
  };

  const changeLeaver = async refs_user_lifecycle_status_id => {
    const newStatus = refs_user_lifecycle_status_id ? 3 : 2;

    try {
      await updateRow("users_master", dealerId, {
        refs_user_lifecycle_status_id: newStatus,
        last_updated_by_auth_id: currentUser.auth_id,
      });
      refreshDisplayedDetails();
    } catch (error) {
      console.error("❌ Error changing leaver status:", error);
    }
  };

  const handleDeleteDealer = async () => {
    setDeletingDealer(true);
    try {
      await updateRow("users_master", dealerId, {
        refs_user_lifecycle_status_id: 4,
        last_updated_by_auth_id: currentUser.auth_id,
      });
      setDeletingDealer(false);
      setShowDeleteConfirmation(false);
      if (window.history && window.history.back) {
        window.history.back();
      }
    } catch (error) {
      console.error("❌ Error deleting dealer:", error);
      setDeletingDealer(false);
    }
  };

  const updateEditFormData = data => {
    setEditFormData(prevData => {
      const updatedData = {
        ...prevData,
        ...data,
      };

      return updatedData;
    });
  };

  const handleEditSubmit = async () => {
    setEditLoading(true);
    setEditError(null);

    try {
      if (!editFormData) {
        throw new Error("No form data available");
      }

      const updateData = {
        // Personal information
        firstName: editFormData.firstName,
        middleName: editFormData.middleName,
        surname: editFormData.surname,
        dateOfBirth: editFormData.dateOfBirth
          ? new Date(editFormData.dateOfBirth * 1000)
              .toISOString()
              .split("T")[0]
          : null,
        refsGender: editFormData.refsGender,
        refsIdentityDocType: editFormData.refsIdentityDocType,
        identificationNo: editFormData.identificationNo,
        nationality: editFormData.nationality,
        operatorId: editFormData.operatorId,
        companyName: editFormData.companyName,
        refsShirtSize: editFormData.refsShirtSize,

        // Contact information
        email: editFormData.email,
        phone: editFormData.phone
          ? formatPhoneNumber(editFormData.phone)
          : null,
        altPhone: editFormData.altPhone
          ? formatPhoneNumber(editFormData.altPhone)
          : null,

        // Address information
        province: editFormData.province,
        street: editFormData.street,
        suburb: editFormData.suburb,
        city: editFormData.city,
        postalCode: editFormData.postalCode,
        country: editFormData.country,

        // Payment information
        refsPaymentType: editFormData.refsPaymentType,
        refsBankAccountType: editFormData.refsBankAccountType,
        eWalletLink: editFormData.eWalletLink,

        // Document URLs
        idPhotoUrl: editFormData.idPhotoUrl,
        personPhotoUrl: editFormData.personPhotoUrl,

        // Passport information
        passportIssueDate: editFormData.passportIssueDate
          ? new Date(editFormData.passportIssueDate * 1000)
              .toISOString()
              .split("T")[0]
          : null,
        passportExpiryDate: editFormData.passportExpiryDate
          ? new Date(editFormData.passportExpiryDate * 1000)
              .toISOString()
              .split("T")[0]
          : null,

        // Banking details
        bank: editFormData.bank,
        bankAccountNumber: editFormData.bankAccountNumber,
        branchCode: editFormData.branchCode,
      };

      const success = await updateUser(dealerId, updateData);

      if (success) {
        setEditDealerOpen(false);
        refreshDisplayedDetails();
      }
      // alert error?
    } catch (err) {
      console.error("❌ Error updating user:", err);
      setEditError(err.message || "Error updating user");
    } finally {
      setEditLoading(false);
    }
  };

  const formatPhoneNumber = phone => {
    if (!phone) return null;
    const digits = phone.replace(/\D/g, "");
    if (digits.startsWith("0")) {
      return `+27${digits.substring(1)}`;
    }
    return `+27${digits}`;
  };

  const handleEditTabChange = (event, newValue) => {
    setEditTabValue(newValue);
  };

  const renderEditTabContent = tabIndex => {
    if (!editFormData) {
      return <CircularProgress />;
    }

    // Helper function to handle tab navigation with data saving
    const handleTabNext = (data, nextTabIndex) => {
      // Merge the incoming data with existing form data
      const mergedData = {
        ...editFormData, // Keep all existing fields
        ...data, // Override with new changes
      };

      // Save the merged data
      updateEditFormData(mergedData);

      // Then move to next tab
      setEditTabValue(nextTabIndex);
    };

    const handleTabBack = (data, prevTabIndex) => {
      // Merge the incoming data with existing form data
      const mergedData = {
        ...editFormData, // Keep all existing fields
        ...data, // Override with new changes
      };

      // Save the merged data
      updateEditFormData(mergedData);

      // Then move to previous tab
      setEditTabValue(prevTabIndex);
    };

    // Also update the updateEditFormData to not just spread but ensure we keep all fields
    const safeUpdateEditFormData = data => {
      const mergedData = {
        ...editFormData, // Keep all existing fields
        ...data, // Override with new changes
      };
      updateEditFormData(mergedData);
    };

    switch (tabIndex) {
      case 0:
        return (
          <PersonalInfoStep
            formData={editFormData}
            onSave={safeUpdateEditFormData}
            onNext={data => handleTabNext(data, 1)}
            submitLabel="Next"
            showBackButton={false}
            isEditing
          />
        );
      case 1:
        return (
          <AddressInfoStep
            formData={editFormData}
            onSave={safeUpdateEditFormData}
            onBack={data => handleTabBack(data, 0)}
            onNext={data => handleTabNext(data, 2)}
            submitLabel="Next"
          />
        );
      case 2:
        return (
          <DocumentsStep
            formData={editFormData}
            onSave={safeUpdateEditFormData}
            onBack={data => handleTabBack(data, 1)}
            onNext={data => handleTabNext(data, 3)}
            submitLabel="Next"
          />
        );
      case 3:
        return (
          <BankingInfoStep
            formData={editFormData}
            onSave={safeUpdateEditFormData}
            onBack={data => handleTabBack(data, 2)}
            onNext={handleEditSubmit}
            submitLabel="Save Changes"
            loading={editLoading}
          />
        );
      default:
        return "Unknown step";
    }
  };

  return {
    // UI State
    editDealerOpen,
    setEditDealerOpen,
    contractsOpen,
    setContractsOpen,
    changeParentOpen,
    setChangeParentOpen,
    changePasswordOpen,
    setChangePasswordOpen,
    suppliersModalOpen,
    setSuppliersModalOpen,
    supportModalOpen,
    setSupportModalOpen,
    creditModalOpen,
    setCreditModalOpen,
    alertModalOpen,
    setAlertModalOpen,
    showDeleteConfirmation,
    setShowDeleteConfirmation,
    deletingDealer,
    userTypeModalOpen,
    setUserTypeModalOpen,

    // Form State - With tabs restored
    editTabValue,
    setEditTabValue,
    editFormData,
    editLoading,
    editError,

    // Status State
    growthLeader,
    isLeader,
    isLeaderBlocked,

    // Actions
    handleGrowthLeaderChange,
    handleIsLeader,
    handleIsLeaderBlock,
    changeLeaver,
    handleDeleteDealer,
    handleEditSubmit,
    updateEditFormData,
    handleEditTabChange,
    renderEditTabContent,
  };
}
